import React from 'react';
import styled from '@emotion/styled';
import { css, SerializedStyles, Theme } from '@emotion/react';
import { rem } from 'polished';
import { Grid, GridItem } from '../../helpers';
import { Link } from '../../primitives';
import {
  mixins,
  shadows,
  spaces,
  colors,
} from '../../core/styles/theme/default';
import { LinkVariant } from '../../primitives/Link/Link';
import { IconInterface, ImageInterface } from '~/types';

const StyledTeaserNavigation = styled.aside`
  display: block;
  position: relative;
`;

const StyledLink = styled(Link)`
  justify-content: flex-start;
  span {
    font-size: ${rem(16)};
    line-height: ${rem(16)};

    ${mixins.MQ('medium')} {
      font-size: ${rem(20)};
      line-height: ${rem(20)};
    }
  }

  .m-richtext & {
    text-decoration: none;
  }
`;

const StyledGrid = styled.div(
  ({ customGrid, theme }: { customGrid?: SerializedStyles; theme?: Theme }) =>
    css`
      display: grid;
      grid-auto-rows: 1fr;

      ${mixins.responsiveStyles({
        'grid-template-columns': {
          zero: 'repeat(1, 1fr)',
          small: 'repeat(2, 1fr)',
          large: 'repeat(4, 1fr)',
        },
        'grid-gap': {
          zero: rem(spaces.alpha),
          medium: rem(spaces.delta),
          ultra: rem(spaces.epsilon),
        },
      })}

      ${customGrid ? customGrid : null}

    ${theme?.stylesExtension?.teaserNavigation?.grid}
    `,
);

const scrollToSection = (e: any, hash: string) => {
  e.preventDefault();

  const element = document.querySelector(hash);

  if (element) {
    const topOffset = document.querySelector('#main-header.static') ? 0 : 120;
    const top =
      element.getBoundingClientRect().top + window.scrollY - topOffset;

    window.scrollTo({ behavior: 'smooth', top });
  }
};

const TeaserNavigation = (teaserNavigationProps: TeaserNavigationProps) => {
  if (teaserNavigationProps === null) return null;

  const {
    items,
    variant = 'emphasizedImageSmall',
    customGrid,
  } = teaserNavigationProps;

  return (
    <StyledTeaserNavigation>
      <Grid columns={{ zero: 'full', small: 12 }}>
        <GridItem size={{ zero: 12, medium: 12, large: 12 }}>
          <StyledGrid customGrid={customGrid}>
            {items &&
              items.map((data: any) => {
                let customProps: any = {};

                switch (variant) {
                  case 'tileIcon':
                    customProps = {
                      teaserIcon: data.teaserImage ? data.teaserImage : {},
                      iconAfter: 'FmdsIconChevronRight',
                    };
                    break;
                  case 'tileIconWithShadow':
                    customProps = {
                      teaserIcon: data.teaserImage ? data.teaserImage : {},
                      iconAfter: 'FmdsIconChevronRight',
                      boxShadow: shadows.tileShadow,
                      backgroundColor: colors.white,
                      color: colors.blueNichtverschwendenLight,
                    };
                    break;
                  default:
                    customProps = {
                      backgroundImage: data.teaserImage
                        ? data.teaserImage
                        : null,
                      iconTop: data.iconTop,
                      iconAfter: 'AnimatedIconArrowRight',
                      className: 'animate',
                    };
                }

                return (
                  <StyledLink
                    key={data.href}
                    linkText={data.navigationTitle}
                    link={{
                      href: data.href,
                      title: data.contentTitle
                        ? `${data.contentTitle}`
                        : `${data.navigationTitle}`,
                    }}
                    variant={variant}
                    {...customProps}
                    {...(data.isAnchorTag && {
                      onClick: (e) => scrollToSection(e, data.href),
                    })}
                  />
                );
              })}
          </StyledGrid>
        </GridItem>
      </Grid>
    </StyledTeaserNavigation>
  );
};

export type TeaserNavigationProps = {
  items: {
    href: string;
    isAnchorTag?: boolean;
    navigationTitle: string;
    contentTitle?: string;
    teaserImage?: ImageInterface;
    iconTop?: IconInterface;
  }[];
  variant?: LinkVariant;
  customGrid?: SerializedStyles;
} | null;

export default TeaserNavigation;
